import React from "react";
import { TableData } from "@euroland/react-table";
import { useMemo } from "react";
import { classNames } from "../../utils";
import { useBreakpoint } from "../../hooks/useResize";
import { KEY_API_CHANGE_COLOR, TOOL_TEMPLATE } from "../../constant/common";
import dayjs from "dayjs";
import { i18n } from "@euroland/libs";
import { formatDate } from "@euroland/libs";
import SharePriceChart from "./SharePriceChart";
import { getCurrencyLabel } from "../../store/utils";
import { renderValueTable } from "../../utils/format-number";
import DownloadButton from "../DownloadButton";
import { useTemplateSetting } from "../../hooks/useCompanySettings";
import SwichAction from "../SwichAction";
import TickerName from "../TickerName";

const getLabelForIcon = (isRise, isDecrease) => {
  if (isRise) return i18n.translate("priceUpLabel");
  if (isDecrease) return i18n.translate("priceDownLabel");
  return undefined;
};

// display date value in table
const renderDateValue = (user, keyDate, keyApi) => {
  // Don't show anything if keyApi is currency or keyDate is changeOfPeriodPercentDate
  if (keyApi === "currency" || keyDate === "changeOfPeriodPercentDate") return "";

  // Show "-" if value is null or all data is null
  if (user.isNullAllData || user[keyDate] === null || user[keyDate] === undefined)
    return i18n.translate("textNoData");

  // Show value
  return formatDate(user[keyDate] || dayjs(), "shortDate");
};

const createTableData = (dataTable, keyTableSharePriceTab) => {
  const header = {
    column_name: { children: "" },
  };
  dataTable.forEach(item => {
    header["column-" + item.instrumentId] = {
      children: (
        <span title={item.shareName}>
          <TickerName shareName={item.shareName} marketAbbreviation={item.marketAbbreviation} />
        </span>
      ),
    };
  });

  const body = keyTableSharePriceTab.map(item => {
    const columnTable = {
      column_name: {
        children: <span className="columnName__table">{item.nameQuadrata}</span>,
      },
    };
    dataTable.forEach(user => {
      const isRise = KEY_API_CHANGE_COLOR.includes(item.keyApi) && user[item.keyApi] > 0;
      const isDecrease = KEY_API_CHANGE_COLOR.includes(item.keyApi) && user[item.keyApi] < 0;

      columnTable["column-" + user.instrumentId] = {
        children: (
          <div
            className={classNames("value-cell", {
              rise: isRise,
              decrease: isDecrease,
            })}
          >
            <div className="value-cell__group">
              <span
                aria-label={getLabelForIcon(isRise, isDecrease)}
                role="img"
                className="value-cell__icon"
              ></span>
              {item.keyApi === "currency"
                ? getCurrencyLabel(user[item.keyApi])
                : renderValueTable(item.keyApi, user)}
            </div>
            <span
              className={classNames("value-cell__date", {
                nullData: user.isNullAllData,
              })}
            >
              {renderDateValue(user, item.keyDate, item.keyApi)}
            </span>
          </div>
        ),
      };
    });

    return columnTable;
  });

  return { header, body };
};

const createTableDataMobile = (dataTable, keyTableSharePriceTab) => {
  const data = dataTable.map(user => {
    const header = {
      column_name: { children: "" },
      ["column-" + user.instrumentId]: {
        children: <>{user.shareName}</>,
      },
    };

    const body = keyTableSharePriceTab.map(item => {
      const isRise = KEY_API_CHANGE_COLOR.includes(item.keyApi) && user[item.keyApi] > 0;
      const isDecrease = KEY_API_CHANGE_COLOR.includes(item.keyApi) && user[item.keyApi] < 0;

      const columnTable = {
        column_name: {
          children: <span className="columnName__table">{item.nameQuadrata}</span>,
        },
      };
      columnTable["column-" + user.instrumentId] = {
        children: (
          <div
            className={classNames("value-cell", {
              rise: isRise,
              decrease: isDecrease,
            })}
          >
            <div className="value-cell__group">
              <span
                aria-label={getLabelForIcon(isRise, isDecrease)}
                role="img"
                className="value-cell__icon"
              ></span>
              {item.keyApi === "currency"
                ? getCurrencyLabel(user[item.keyApi])
                : renderValueTable(item.keyApi, user)}
            </div>
            <span className="value-cell__date">{renderDateValue(user, item.keyDate, item.keyApi)}</span>
          </div>
        ),
      };
      return columnTable;
    });

    return {
      header,
      body,
      nameTable: <TickerName shareName={user.shareName} marketAbbreviation={user.marketAbbreviation} />,
    };
  });

  return data;
};

const SharePriceTabQuadrata = ({ listTradeBaseOnPeriodState, isSingle }) => {
  const { isMobileScreen } = useBreakpoint();
  const { listTradeBaseOnPeriod = [] } = listTradeBaseOnPeriodState;

  const template = useTemplateSetting();

  const isQuadrata = template === TOOL_TEMPLATE.quadrata;

  // let multiple = !isMobileScreen ? createTableData() : createTableDataMobile();

  const keyTableSharePriceTab = [
    {
      nameQuadrata: i18n.translate("changePeriodlabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("changePeriodlabelTableSharePriceVictor"),
      keyApi: "changeOfPeriodPercent",
      keyDate: "changeOfPeriodPercentDate",
    },
    {
      nameQuadrata: i18n.translate("highPriceLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highPriceLabelTableSharePriceVictor"),
      keyApi: "highestPrice",
      keyDate: "highestPriceDate",
    },
    {
      nameQuadrata: i18n.translate("lowPriceLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowPriceLabelTableSharePriceVictor"),
      keyApi: "lowestPrice",
      keyDate: "lowestPriceDate",
    },
    {
      nameQuadrata: i18n.translate("highVolumeLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highVolumeLabelTableSharePriceVictor"),
      keyApi: "highestVolume",
      keyDate: "highestVolumeDate",
    },
    {
      nameQuadrata: i18n.translate("lowVolumeLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowVolumeLabelTableSharePriceVictor"),
      keyApi: "lowestVolume",
      keyDate: "lowestVolumeDate",
    },
    {
      nameQuadrata: i18n.translate("highDailyLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highDailyLabelTableSharePriceVictor"),
      keyApi: "highestDailyChangePercent",
      keyDate: "highestDailyChangePercentDate",
    },
    {
      nameQuadrata: i18n.translate("lowDailyLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowDailyLabelTableSharePriceVictor"),
      keyApi: "lowestDailyChangePercent",
      keyDate: "lowestDailyChangePercentDate",
    },
  ];

  const dataTable = useMemo(
    () =>
      !isMobileScreen
        ? createTableData(listTradeBaseOnPeriod, keyTableSharePriceTab)
        : createTableDataMobile(listTradeBaseOnPeriod, keyTableSharePriceTab),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listTradeBaseOnPeriod, isMobileScreen]
  );
  if (!listTradeBaseOnPeriod?.length) return null;
  return (
    <>
      <SwichAction />
      <div
        className={classNames("share-price__content", {
          "multiple-trade": !isSingle,
        })}
      >
        <SharePriceChart isSingle={isSingle} />
        {isMobileScreen ? (
          <>
            {dataTable.map((item, indKey) => (
              <TableData
                className={classNames("share-price__table share-price-quadrata one-user hidden-header", {
                  "first-child-mobile": indKey === 0,
                  // "table-skeleton": !!loading,
                })}
                note={item.nameTable}
                header={item.header}
                body={item.body}
                key={indKey}
                caption={item.nameTable}
                containerProps={{}}
              />
            ))}
          </>
        ) : (
          <div className="share-price__table">
            <TableData
              className={classNames("share-price__table share-price-quadrata", {
                "one-user": listTradeBaseOnPeriod.length === 1,
                "multiple-user-pc": listTradeBaseOnPeriod.length > 1,
                "hidden-header": listTradeBaseOnPeriod.length === 1,
              })}
              // note={listTradeBaseOnPeriod.length === 1 && listTradeBaseOnPeriod?.[0]?.shareName}
              header={dataTable.header}
              body={dataTable.body}
              caption={
                listTradeBaseOnPeriod.length === 1
                  ? listTradeBaseOnPeriod?.[0]?.shareName
                  : i18n.translate("noteTable")
              }
              containerProps={{}}
            />
          </div>
        )}
      </div>
      <div className="share-price__download--outside">
        <DownloadButton />
      </div>
    </>
  );
};

export default SharePriceTabQuadrata;
